import React from 'react';
import { 
  Users, 
  Calendar,
  Activity,
  Award,
  BarChart3,
  Target,
  CheckCircle,
  UserCheck,
  AlertCircle,
  Rocket
} from 'lucide-react';

const CustomerEngagementReport = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Users className="w-8 h-8 text-blue-600" />
          Customer Engagement Summary
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            Oct 2 – Nov 24, 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Engagement Analysis
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Overview */}
        <div className="report-section">
          <h2 className="section-title">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Overview
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Total Users</h3>
                <span className="status-badge completed">9 USERS</span>
              </div>
              <div className="metrics-grid" style={{ marginTop: '16px' }}>
                <div className="metric">
                  <span className="metric-value">9</span>
                  <span className="metric-label">Total Users</span>
                </div>
                <div className="metric">
                  <span className="metric-value">56%</span>
                  <span className="metric-label">Weekly Active Rate</span>
                </div>
                <div className="metric">
                  <span className="metric-value">5</span>
                  <span className="metric-label">Active in Last 7 Days</span>
                </div>
              </div>
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                  <strong>Reporting Period:</strong> October 2 – November 24, 2025
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Improvements */}
        <div className="report-section">
          <h2 className="section-title">
            <Rocket className="w-6 h-6 text-purple-600" />
            Platform Improvements (Past 1.5 Months)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Data Extraction & Accuracy Fixes</h3>
                <span className="status-badge completed">IMPROVED</span>
              </div>
              <ul className="achievement-list">
                <li>Resolved page number display and accuracy issues across all document types</li>
                <li>Enhanced exclusion form data extraction for improved completeness</li>
                <li>Fixed Deductible/SIR limits extraction and validation errors</li>
                <li>Improved Workers' Compensation (WC) and endorsement detail extraction in Binders and Quotes</li>
                <li>Implemented numerous incremental improvements to Quotes and Binders extraction, resulting in noticeable product quality enhancements</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Coverage Check Enhancements</h3>
                <span className="status-badge completed">ENHANCED</span>
              </div>
              <ul className="achievement-list">
                <li>Added Additional First Name Insured field to coverage verification</li>
                <li>Implemented schedule matching endorsement verification</li>
                <li>Added Additional Named Insureds endorsements tracking</li>
                <li>Integrated Broad Form Named Insured identification</li>
                <li>Expanded coverage standards library using Standards provided by Baldwin</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Comparison & Display Features</h3>
                <span className="status-badge completed">NEW</span>
              </div>
              <ul className="achievement-list">
                <li>Added Attached/Not-Attached status indicator in Document Comparison</li>
                <li>Added Attached/Not-Attached status indicator in Coverage Check</li>
                <li>Improved data visibility and presentation across comparison views</li>
              </ul>
            </div>
          </div>
        </div>

        {/* High Engagement Users */}
        <div className="report-section">
          <h2 className="section-title">
            <Award className="w-6 h-6 text-yellow-600" />
            High Engagement Users (100+ Actions)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Patricia Fazzino</h3>
                <span className="status-badge completed">379 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 379 across 7 active days</li>
                <li><strong>Last Active:</strong> November 20</li>
                <li><strong>Primary Usage:</strong> Quote/Binder/Policy (4x), Document Comparison (2x)</li>
                <li><strong>Activity:</strong> 7 logins, 5 PDF reports generated</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Gabriel Stellar</h3>
                <span className="status-badge completed">274 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 274 across 10 active days</li>
                <li><strong>Last Active:</strong> October 23</li>
                <li><strong>Primary Usage:</strong> Document Comparison (4x)</li>
                <li><strong>Activity:</strong> 10 logins, 1 PDF report generated</li>
                <li style={{ color: '#f59e0b', fontWeight: '600' }}>
                  <AlertCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Note:</strong> Inactive for 32 days
                </li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Christine Tobin</h3>
                <span className="status-badge completed">209 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 209 across 5 active days</li>
                <li><strong>Last Active:</strong> October 28</li>
                <li><strong>Primary Usage:</strong> Quote/Binder/Policy (1x), Document Comparison (2x)</li>
                <li><strong>Activity:</strong> 1 login, 3 PDF reports generated</li>
                <li style={{ color: '#f59e0b', fontWeight: '600' }}>
                  <AlertCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Note:</strong> Inactive for 27 days
                </li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Amy Klein</h3>
                <span className="status-badge completed">205 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 205 across 10 active days</li>
                <li><strong>Last Active:</strong> November 18</li>
                <li><strong>Primary Usage:</strong> Quote/Binder/Policy (8x), Document Comparison (6x)</li>
                <li><strong>Activity:</strong> 7 logins, 6 PDF reports generated</li>
                <li><strong>Note:</strong> Highest feature adoption across platform</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Danielle McCabe</h3>
                <span className="status-badge completed">125 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 125 across 5 active days</li>
                <li><strong>Last Active:</strong> November 24 (current day)</li>
                <li><strong>Primary Usage:</strong> Quote/Binder/Policy (3x), Document Comparison (1x), Coverage Check (2x)</li>
                <li><strong>Activity:</strong> 4 logins, 2 PDF reports generated</li>
                <li><strong>Note:</strong> Only user utilizing Coverage Check feature</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Moderate Engagement Users */}
        <div className="report-section">
          <h2 className="section-title">
            <Activity className="w-6 h-6 text-orange-600" />
            Moderate Engagement Users (50-99 Actions)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Jason Rohde</h3>
                <span className="status-badge in-progress">93 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 93 across 1 active day</li>
                <li><strong>Last Active:</strong> October 16</li>
                <li style={{ color: '#f59e0b', fontWeight: '600' }}>
                  <AlertCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Status:</strong> Inactive (39 days)
                </li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Jose Grullon</h3>
                <span className="status-badge in-progress">53 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 53 across 2 active days</li>
                <li><strong>Last Active:</strong> November 19</li>
                <li style={{ color: '#10b981', fontWeight: '600' }}>
                  <CheckCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Status:</strong> Active
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Light Engagement Users */}
        <div className="report-section">
          <h2 className="section-title">
            <UserCheck className="w-6 h-6 text-green-600" />
            Light Engagement Users (Under 50 Actions)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Mike Zirpoli</h3>
                <span className="status-badge planning">45 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 45 across 3 active days</li>
                <li><strong>Last Active:</strong> November 17</li>
                <li style={{ color: '#10b981', fontWeight: '600' }}>
                  <CheckCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Status:</strong> Active
                </li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Leandra Gallina</h3>
                <span className="status-badge planning">22 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Actions:</strong> 22 across 1 active day</li>
                <li><strong>Last Active:</strong> October 13</li>
                <li style={{ color: '#f59e0b', fontWeight: '600' }}>
                  <AlertCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  <strong>Status:</strong> Inactive (42 days)
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Takeaways */}
        <div className="report-section">
          <h2 className="section-title">
            <Target className="w-6 h-6 text-teal-600" />
            Key Takeaways
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Engagement Insights</h3>
                <span className="status-badge completed">SUMMARY</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Top performers:</strong> Patricia Fazzino and Amy Klein drive the majority of platform engagement with consistent multi-day usage</li>
                <li><strong>Re-engagement opportunity:</strong> Gabriel Stellar, Christine Tobin, Jason Rohde, and Leandra Gallina have not logged in for 27+ days despite prior activity</li>
                <li><strong>Feature adoption:</strong> Quote/Binder/Policy and Document Comparison are the primary features; Coverage Check remains underutilized (only 1 user)</li>
                <li><strong>PDF generation:</strong> 20 total reports generated across 7 users</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>User Distribution</h3>
                <span className="status-badge completed">BREAKDOWN</span>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>High Engagement (100+ Actions)</span>
                  <span style={{ color: '#3b82f6', fontWeight: '600' }}>5 users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Moderate Engagement (50-99 Actions)</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>2 users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Light Engagement (Under 50 Actions)</span>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>2 users</span>
                </div>
              </div>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Activity Status</h3>
                <span className="status-badge completed">STATUS</span>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Active Users</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>5 users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Inactive Users (27+ days)</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>4 users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="report-footer">
        <p><strong>Prepared by:</strong> Analytics Team</p>
        <p><strong>Report Date:</strong> November 24, 2025</p>
      </div>
    </div>
  );
};

export default CustomerEngagementReport;
